import React, { useEffect, useReducer, useState } from 'react';
import { nextSortDirection } from '../../utils/tableSort';
import { Helmet } from 'react-helmet-async';
import { FaFileExcel, FaPlus } from 'react-icons/fa';
import { MdOutlineMoney } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import FilterForm from '../../components/FilterForm';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import MessageBox from '../../components/MessageBox';
import creditService from '../../services/credit.service';
import CreditConceptService from '../../services/creditConcept.service';
import { getError, humanizeLabel, parsePeriodicity, showCurrency } from '../../utils';
import {
	Button,
	DataTable,
	EntityList,
	ExportModal,
	IdTile,
	ListToolbar,
	PageHead,
	Pill,
	RowActionsMenu,
	buildXlsxExport,
	exportAmount,
} from '../../components/EntityList';
import { CreditDeletionModal } from '../../components/CascadeDeletionModal';
import Paginator from '../Paginator/Paginator';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			credits: action.payload.credits,
			page: action.payload.page,
			pages: action.payload.pages,
			countCredits: action.payload.countCredits,
			pageSize: action.payload.pageSize,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };

	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const renderEmpty = () => <span className='tw:text-ink-3'>—</span>;

const formatDate = (date) => {
	if (!date) {
		return null;
	}
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	return parsed.toLocaleDateString('es-UY', { timeZone: 'UTC' });
};

const formatAmount = (credit, value) => {
	if (!hasValue(value)) {
		return null;
	}
	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return null;
	}
	const currency = showCurrency(credit.currency) || credit.currency || '';
	return `${currency} ${amount.toLocaleString('es-UY')}`.trim();
};

// Helper function to convert concept name to code format
const normalizeConceptName = (name) => {
	return name
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '') // Remove accents
		.replace(/\s+/g, '-'); // Replace spaces with hyphens
};

function CreditList() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { canSee, canEdit, canCreate, canDelete } = usePermissions();

	const [{ loading, error, credits, successDelete, pageSize, countCredits }, dispatch] =
		useReducer(reducer, {
			credits: [],
			loading: true,
			error: '',
			pageSize: 0,
			countCredits: 0,
		});
	const initialLoading = loading && pageSize === 0;

	const location = useLocation();
	const { search } = location;
	const sp = new URLSearchParams(search); // /search?category=Shirts
	const column = sp.get('column') || 'customId';
	const direction = sp.get('direction') || 'asc';
	const concept = sp.get('concept') || 'all';
	const periodicity = sp.get('periodicity') || 'all';
	const currency = sp.get('currency') || 'all';
	const amountTo = sp.get('amountTo') || '';
	const amountFrom = sp.get('amountFrom') || 0;
	const dateFrom = sp.get('dateFrom') || '';
	const dateTo = sp.get('dateTo') || '';
	const endDateFrom = sp.get('endDateFrom') || '';
	const endDateTo = sp.get('endDateTo') || '';
	const query = sp.get('query') || 'all';
	const page = sp.get('page') || 1;

	const [concepts, setConcepts] = useState([]);
	const [conceptsLoading, setConceptsLoading] = useState(true);

	const [isOpen, setIsOpen] = useState(false);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [deletingCredit, setDeletingCredit] = useState(null);

	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};

	const [filterOptions, setFilterOptions] = useState({
		formType: 'Pasividades',
		concept: concept,
		periodicity: periodicity,
		dateFrom: dateFrom,
		dateTo: dateTo,
		endDateFrom: endDateFrom,
		endDateTo: endDateTo,
		amountFrom: amountFrom,
		amountTo: amountTo,
		currency: currency,
		query: query,
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	// `filterOptions` seeds from the URL on mount only, but the toolbar search,
	// the concept chips, sorting and pagination all navigate without touching
	// it. Since getFilterUrl() rebuilds every URL out of filterOptions, a stale
	// value silently dropped the *other* active filters — searching and then
	// clicking a concept chip discarded the search term, so a single concept
	// could return more rows than "Todas". Re-sync whenever the URL changes.
	useEffect(() => {
		setFilterOptions({
			formType: 'Pasividades',
			concept,
			periodicity,
			dateFrom,
			dateTo,
			endDateFrom,
			endDateTo,
			amountFrom,
			amountTo,
			currency,
			query,
		});
	}, [concept, periodicity, dateFrom, dateTo, endDateFrom, endDateTo, amountFrom, amountTo, currency, query]);


	const getFilterUrl = (filter) => {
		const {
			concept,
			periodicity,
			query,
			dateFrom,
			dateTo,
			endDateFrom,
			endDateTo,
			amountFrom,
			amountTo,
			currency,
		} = filterOptions;
		const filterConcept = filter.concept || concept;
		const filterPeriodicity = periodicity || '';
		const filterQuery = filter.query || query;
		const filterPage = filter.page || 1;
		const filterDateFrom = dateFrom || '';
		const filterDateTo = dateTo || '';
		const filterEndDateFrom = endDateFrom || '';
		const filterEndDateTo = endDateTo || '';
		const filterAmountFrom = amountFrom || '';
		const filterAmountTo = amountTo || '';
		const filterCurrency = currency || '';

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('concept', filterConcept);
		params.append('periodicity', filterPeriodicity);
		params.append('query', filterQuery);
		params.append('dateFrom', filterDateFrom);
		params.append('dateTo', filterDateTo);
		params.append('endDateFrom', filterEndDateFrom);
		params.append('endDateTo', filterEndDateTo);
		params.append('amountFrom', filterAmountFrom);
		params.append('amountTo', filterAmountTo);
		params.append('currency', filterCurrency);
		// Without these the sort silently reset to the default on every filter
		// change, search and page step.
		params.append('column', column);
		params.append('direction', direction);

		return `/AdminScreen/credits/search?${params.toString()}`;
	};

	const handleConceptInputChange = (value) => {
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			concept: value,
		}));
		navigate(getFilterUrl({ concept: value, page: 1 }));
	};

	const handleApplyFilter = () => {
		navigate(getFilterUrl({ page: 1 }));
	};

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'Pasividades',
			concept: 'all',
			periodicity: 'all',
			dateFrom: '',
			dateTo: '',
			endDateFrom: '',
			endDateTo: '',
			amountFrom: 0,
			amountTo: '',
			currency: 'all',
			query: 'all',
		});
	};

	const onPageChange = (page) => {
		navigate(getFilterUrl({ page: page }));
	};

	const fetchCreditConcepts = async () => {
		try {
			const result = await CreditConceptService.list({ active: true });
			const activeConcepts = result.data
				.sort((a, b) => (a.order || 0) - (b.order || 0));

			// Transform concepts to the format expected by the filter. The stored
			// name can be a raw slug ('prestamos-amortizable'), so the chip label
			// is humanized for display — the value stays the normalized slug.
			const conceptOptions = [
				{ label: 'Todas', value: 'all' },
				...activeConcepts
					.filter(c => c.name) // Filter out concepts without a name
					.map(c => ({
						label: humanizeLabel(c.name),
						value: normalizeConceptName(c.name)
					}))
			];

			setConcepts(conceptOptions);
		} catch (error) {
			console.error('Error fetching credit concepts:', error);
			toast.error('No se pudieron cargar los conceptos');
		} finally {
			setConceptsLoading(false);
		}
	};

	useEffect(() => {
		fetchCreditConcepts();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await creditService.search(
					`page=${page}&concept=${concept}&periodicity=${periodicity}&query=${query}&column=${column}&direction=${direction}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}&endDateFrom=${endDateFrom}&endDateTo=${endDateTo}`,
				);
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
				console.error(getError(err));
				toast.error('No se pudo obtener lista');
			}
		};
		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		} else {
			fetchData();
		}
	}, [
		successDelete,
		page,
		direction,
		column,
		concept,
		periodicity,
		dateFrom,
		dateTo,
		endDateFrom,
		endDateTo,
		amountFrom,
		amountTo,
		currency,
		query,
	]);

	// The cascade modal previews everything the delete takes with it.
	function deleteCreditHandler(credit) {
		setDeletingCredit(credit);
	}

	const handleSort = (sortColumn) => {
		const newSortDirection = nextSortDirection(column, direction, sortColumn);
		const newURL = `/AdminScreen/credits/search?page=${page}&concept=${concept}&periodicity=${periodicity}&query=${query}&column=${sortColumn}&direction=${newSortDirection}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}&endDateFrom=${endDateFrom}&endDateTo=${endDateTo}`;
		navigate(newURL);
	};

	async function editCreditHandler(credit) {
		navigate(`/AdminScreen/credits/editCredit/${credit._id}`);
	}
	async function seeDetails(credit) {
		navigate(`/AdminScreen/credits/credit/${credit._id}`);
	}
	async function newCreditHandler() {
		navigate('/AdminScreen/newCredit');
	}
	async function newPaymentHandler() {
		const returnTo = encodeURIComponent(location.pathname + location.search);
		navigate(`/AdminScreen/newCreditPayment?returnTo=${returnTo}`);
	}

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter('Pasividades'),
		handleApplyFilter: () => handleApplyFilter('Pasividades')
	};

	const renderMoney = (credit, value) => {
		const formatted = formatAmount(credit, value);
		return formatted ? (
			<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{formatted}</span>
		) : (
			renderEmpty()
		);
	};

	const renderDate = (date) => {
		const formatted = formatDate(date);
		return formatted ? (
			<span className='tw:font-mono tw:text-[13px] tw:tabular-nums tw:text-ink-2'>{formatted}</span>
		) : (
			renderEmpty()
		);
	};

	const exportFields = [
		{ key: 'customId', label: t('field.accountConcept'), locked: true },
		{ key: 'owner.name', label: t('entity.account.creditor') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'capital', label: t('field.capital') },
		{ key: 'concept', label: t('entity.concept') },
		{ key: 'dues', label: t('entity.due.plural') },
		{ key: 'amount', label: t('field.dueAmount') },
		{ key: 'date', label: t('field.date') },
		{ key: 'endDate', label: t('field.endDate') },
		{ key: 'periodicity', label: t('field.periodicity') },
		{ key: 'guaranteedAmount', label: t('field.totalGuaranteed') },
	];

	const exportFieldGetters = {
		customId: (credit) => credit.customId || '',
		'owner.name': (credit) => credit.owner?.name || '',
		currency: (credit) => credit.currency || '',
		capital: (credit) => exportAmount(credit.capital),
		concept: (credit) => credit.concept || '',
		dues: (credit) => credit.dues ?? '',
		amount: (credit) => exportAmount(credit.amount),
		date: (credit) => formatDate(credit.date) || '',
		endDate: (credit) => formatDate(credit.endDate) || '',
		periodicity: (credit) => (credit.periodicity ? parsePeriodicity(credit.periodicity.toString()) : ''),
		guaranteedAmount: (credit) => exportAmount(credit.guaranteedAmount),
	};

	// Refetches the whole filtered set in one page (the route has no pageSize
	// cap) carrying the current column/direction, so the sheet matches what the
	// user is looking at rather than just the visible page.
	async function handleExportCredits(selectedKeys) {
		const { data } = await creditService.search(
			`page=1&concept=${concept}&periodicity=${periodicity}&query=${query}&column=${column}&direction=${direction}&currency=${currency}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}&endDateFrom=${endDateFrom}&endDateTo=${endDateTo}&pageSize=${countCredits || 1}`,
		);
		return buildXlsxExport({
			rows: data.credits,
			fields: exportFields,
			selectedKeys,
			getters: exportFieldGetters,
			sheetName: 'Pasividades',
			filePrefix: 'pasividades',
		});
	}

	const renderRowActions = (credit) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(credit) },
				canEdit() && { label: 'Editar', onClick: () => editCreditHandler(credit) },
				canDelete() && { label: 'Eliminar', onClick: () => deleteCreditHandler(credit), danger: true },
			]}
		/>
	);

	const creditTableColumns = [
		{
			key: 'customId',
			header: t('field.accountConcept'),
			sortable: true,
			render: (credit) => (
				<div className='tw:flex tw:items-center tw:gap-2.5'>
					<IdTile name={credit.customId || credit.concept} />
					<div>
						<div className='tw:font-medium tw:text-ink-0'>{credit.customId || '—'}</div>
						{credit.owner?.name && (
							<div className='tw:text-[11.5px] tw:text-ink-3'>{credit.owner.name}</div>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'owner.name',
			header: t('entity.account.creditor'),
			sortable: true,
			render: (credit) => {
				const ownerName = credit.owner?.name;
				return ownerName ? (
					<div className='tw:flex tw:items-center tw:gap-2'>
						<IdTile name={ownerName} size={22} shape='circle' />
						<span className='tw:text-[12.5px] tw:text-ink-2'>{ownerName}</span>
					</div>
				) : (
					renderEmpty()
				);
			},
		},
		{
			key: 'capital',
			header: t('field.capital'),
			sortable: true,
			className: 'tw:text-right',
			render: (credit) => renderMoney(credit, credit.capital),
		},
		{
			key: 'concept',
			header: t('entity.concept'),
			sortable: true,
			render: (credit) => credit.concept ? <Pill tone='accent'>{credit.concept}</Pill> : renderEmpty(),
		},
		{
			key: 'dues',
			header: t('entity.due.plural'),
			sortable: true,
			className: 'tw:text-right',
			render: (credit) => hasValue(credit.dues) ? (
				<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{credit.dues}</span>
			) : (
				renderEmpty()
			),
		},
		{
			key: 'amount',
			header: t('field.dueAmount'),
			sortable: true,
			className: 'tw:text-right',
			render: (credit) => renderMoney(credit, credit.amount),
		},
		{
			key: 'date',
			header: t('field.date'),
			sortable: true,
			render: (credit) => renderDate(credit.date),
		},
		{
			key: 'endDate',
			header: t('field.endDate'),
			sortable: true,
			render: (credit) => renderDate(credit.endDate),
		},
		{
			key: 'periodicity',
			header: t('field.periodicity'),
			sortable: true,
			render: (credit) => credit.periodicity ? (
				<span className='tw:text-[12.5px] tw:text-ink-2'>{parsePeriodicity(credit.periodicity.toString())}</span>
			) : (
				renderEmpty()
			),
		},
		{
			key: 'guaranteedAmount',
			header: t('field.totalGuaranteed'),
			sortable: true,
			className: 'tw:text-right',
			render: (credit) => renderMoney(credit, credit.guaranteedAmount),
		},
	];

	return error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<div>
			<Helmet>
				<title>{`Lista de ${t('entity.credit.plural')}`}</title>
			</Helmet>

			<FilterForm {...filterFormProps} />

			<EntityList>
				<PageHead
					title={t('entity.credit.plural')}
					subtitle={`${initialLoading ? '—' : countCredits} ${t('entity.credit.plural').toLowerCase()} encontradas`}
					actions={
						<>
							<Button variant='default' icon={<FaFileExcel />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
							{canCreate() && (
								<Button variant='primary' icon={<FaPlus />} onClick={newCreditHandler}>
									{`Agregar ${t('entity.credit').toLowerCase()}`}
								</Button>
							)}
							{canCreate() && (
								<Button icon={<MdOutlineMoney />} onClick={newPaymentHandler}>
									Agregar Pago
								</Button>
							)}
						</>
					}
				/>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Buscar por pasividad, deudor, concepto o fecha…'
					segments={concepts}
					activeSegment={filterOptions.concept}
					onSegmentChange={handleConceptInputChange}
					segmentsLoading={conceptsLoading}
					onAdvancedFilter={handleDropdownToggle}
					resultCount={initialLoading ? null : countCredits}
				/>
				<DataTable
					columns={creditTableColumns}
					rows={credits}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/creditRoutes.js PAGE_SIZE
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(credit) => seeDetails(credit)}
					rowActions={canEdit() || canDelete() ? renderRowActions : undefined}
					emptyMessage='No se encontraron pasividades con los filtros aplicados.'
				/>
				<Paginator
					className='tw:mt-4'
					totalCount={countCredits}
					pageSize={pageSize}
					currentPage={parseInt(page)}
					onPageChange={onPageChange}
				/>
			</EntityList>

			<CreditDeletionModal
				show={Boolean(deletingCredit)}
				record={deletingCredit}
				onClose={() => setDeletingCredit(null)}
				onDeleted={() => {
					setDeletingCredit(null);
					dispatch({ type: 'DELETE_SUCCESS' });
				}}
			/>

			<ExportModal
				show={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				count={countCredits}
				fields={exportFields}
				subtitle={`${t('entity.credit.plural')} — se exportará el listado filtrado actual`}
				onExport={handleExportCredits}
			/>
		</div>
	);
}

export default CreditList;
